import { Spinner } from '@/components/ui/spinner'
import { TableCard, TableCardField } from '@/components/ui/table-card'
import { DEFAULT_FILE_ICON, FILE_ICONS, fileExtension } from '@/lib/fileIcon'
import { documentTypeLabel, PARSE_PENDING } from '@/models/document'
import { DocumentActions } from './DocumentActions'
import { AttachmentLink, documentSubline, soleAttachment } from './DocumentRow'
import { expiryClass, formatDate, formatFileSize } from './format'
import { type DocumentsTableDocument, totalFileSize, uploadedAt } from './types'
import type { DocumentRowActions } from './useDocumentRowActions'

function ExpiryValue({ document }: { document: DocumentsTableDocument }) {
  return <span className={expiryClass(document.dateExpiration)}>{formatDate(document.dateExpiration)}</span>
}

function Parsing() {
  return (
    <span className="text-muted-foreground flex items-center justify-end gap-2">
      <Spinner className="size-3.5" />
      Reading document…
    </span>
  )
}

function DocumentCard({
  document,
  openDetails,
  openRename,
  openPreview,
}: { document: DocumentsTableDocument } & DocumentRowActions) {
  const single = soleAttachment(document)
  const subline = documentSubline(document)
  const isPending = document.parseStatus === PARSE_PENDING

  return (
    <TableCard
      title={
        single ? (
          <AttachmentLink
            document={document}
            attachment={single}
            label={document.displayName}
            openPreview={openPreview}
          />
        ) : (
          document.displayName
        )
      }
      actions={<DocumentActions document={document} openDetails={openDetails} openRename={openRename} />}
    >
      <TableCardField label="Type" value={documentTypeLabel(document.documentType)} />
      {subline && <TableCardField label="Files" value={subline} />}
      {isPending ? (
        <TableCardField label="Dates" value={<Parsing />} />
      ) : (
        <>
          <TableCardField label="Effective" value={formatDate(document.dateEffective)} />
          <TableCardField label="Expires" value={<ExpiryValue document={document} />} />
        </>
      )}
      <TableCardField label="Size" value={formatFileSize(totalFileSize(document))} />
      <TableCardField label="Uploaded" value={formatDate(uploadedAt(document))} />
      {document.attachments.length > 1 &&
        document.attachments.map((attachment) => {
          const Icon = FILE_ICONS[fileExtension(attachment.fileName)] ?? DEFAULT_FILE_ICON
          return (
            <TableCardField
              key={attachment.id}
              label={formatFileSize(attachment.fileSize)}
              value={
                <span className="flex items-center justify-end gap-2">
                  <Icon className="text-muted-foreground size-4 shrink-0" />
                  <AttachmentLink document={document} attachment={attachment} openPreview={openPreview} />
                </span>
              }
            />
          )
        })}
    </TableCard>
  )
}

/** Narrow-screen stand-in for the rows: one card per document. */
export function DocumentCards({
  documents,
  openDetails,
  openRename,
  openPreview,
}: { documents: DocumentsTableDocument[] } & DocumentRowActions) {
  return (
    <>
      {documents.map((document) => (
        <DocumentCard
          key={document.id}
          document={document}
          openDetails={openDetails}
          openRename={openRename}
          openPreview={openPreview}
        />
      ))}
    </>
  )
}
